// Performance Manager
// Architecture: v4.0.0 Multiple Charts Implementation
// Memory management and performance optimizations

import { devLog, devError } from '../../../utils/devLog';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useMultipleCharts } from './MultipleChartsContext';
import { ChartInstance } from '../../../types/MultipleCharts';

/**
 * Performance Metrics Interface
 */
export interface PerformanceMetrics {
  memoryUsage: {
    totalHeapSize: number;
    usedHeapSize: number;
    heapSizeLimit: number;
  };
  chartMetrics: {
    totalCharts: number;
    loadedCharts: number;
    activeRenderTime: number;
    lastGcTime: Date | null;
  };
  renderMetrics: {
    frameRate: number;
    renderTime: number;
    elementCount: number;
  };
}

/**
 * Memory Management Configuration
 */
export interface MemoryConfig {
  maxIdleCharts: number;           // Maximum idle charts before cleanup
  memoryThreshold: number;         // Memory threshold (MB) for cleanup
  gcInterval: number;              // Garbage collection check interval (ms)
  chartCacheSize: number;          // Maximum cached chart data
  preloadNextChart: boolean;       // Preload adjacent charts
  lazyUnloadDelay: number;         // Delay before unloading inactive charts (ms)
  enableMemoryMonitoring: boolean; // Enable memory monitoring
}

/**
 * Default memory management configuration
 */
const DEFAULT_MEMORY_CONFIG: MemoryConfig = {
  maxIdleCharts: 5,     // Allow 5 idle charts
  memoryThreshold: 50,  // 50MB threshold - aggressive cleanup for memory efficiency
  gcInterval: 10000,    // 10 seconds (frequent cleanup)
  chartCacheSize: 2,    // Minimal cache due to memory constraints
  preloadNextChart: false, // Disabled preloading to save memory
  lazyUnloadDelay: 14400000,  // 4 hours (4 * 60 * 60 * 1000)
  enableMemoryMonitoring: process.env.NODE_ENV === 'development'
};

/**
 * Performance Manager Hook
 * Manages memory usage and performance optimizations
 */
export function usePerformanceManager(config: Partial<MemoryConfig> = {}) {
  const {
    charts,
    loadedChartIds,
    activeChartId,
    unloadChart
  } = useMultipleCharts();
  
  const [memoryConfig, setMemoryConfig] = useState<MemoryConfig>({
    ...DEFAULT_MEMORY_CONFIG,
    ...config
  });
  
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>({
    memoryUsage: {
      totalHeapSize: 0,
      usedHeapSize: 0,
      heapSizeLimit: 0
    },
    chartMetrics: {
      totalCharts: 0,
      loadedCharts: 0,
      activeRenderTime: 0,
      lastGcTime: null
    },
    renderMetrics: {
      frameRate: 60,
      renderTime: 0,
      elementCount: 0
    }
  });
  
  const [lastActivityTime, setLastActivityTime] = useState<Map<string, Date>>(new Map());
  const gcTimerRef = useRef<NodeJS.Timeout | null>(null);
  const frameCountRef = useRef(0);
  const lastFrameTimeRef = useRef(performance.now());
  
  /**
   * Get memory usage information
   */
  const getMemoryUsage = useCallback((): PerformanceMetrics['memoryUsage'] => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        totalHeapSize: memory.totalJSHeapSize || 0,
        usedHeapSize: memory.usedJSHeapSize || 0,
        heapSizeLimit: memory.jsHeapSizeLimit || 0
      };
    }
    
    // Fallback estimation - Target after memory leak fixes (~20MB per chart)
    return {
      totalHeapSize: charts.size * 25 * 1024 * 1024, // Target 25MB per chart total
      usedHeapSize: charts.size * 20 * 1024 * 1024,  // Target 20MB per chart used
      heapSizeLimit: 2048 * 1024 * 1024 // 2GB limit estimation for mobile compatibility
    };
  }, [charts.size]);
  
  /**
   * Calculate frame rate
   */
  const updateFrameRate = useCallback(() => {
    const now = performance.now();
    const delta = now - lastFrameTimeRef.current;
    
    if (delta >= 1000) { // Update every second
      const fps = Math.round((frameCountRef.current * 1000) / delta);
      
      setPerformanceMetrics(prev => ({
        ...prev,
        renderMetrics: {
          ...prev.renderMetrics,
          frameRate: fps
        }
      }));
      
      frameCountRef.current = 0;
      lastFrameTimeRef.current = now;
    } else {
      frameCountRef.current++;
    }
    
    if (memoryConfig.enableMemoryMonitoring) {
      requestAnimationFrame(updateFrameRate);
    }
  }, [memoryConfig.enableMemoryMonitoring]);
  
  /**
   * Identify idle charts for cleanup
   */
  const getIdleCharts = useCallback((): string[] => {
    const now = new Date();
    const idleCharts: string[] = [];
    
    for (const chartId of loadedChartIds) {
      if (chartId === activeChartId) continue; // Never unload active chart
      
      const lastActivity = lastActivityTime.get(chartId);
      if (lastActivity) {
        const idleTime = now.getTime() - lastActivity.getTime();
        if (idleTime > memoryConfig.lazyUnloadDelay) {
          idleCharts.push(chartId);
        }
      }
    }
    
    return idleCharts.slice(0, Math.max(0, loadedChartIds.length - memoryConfig.maxIdleCharts));
  }, [loadedChartIds, activeChartId, lastActivityTime, memoryConfig]);
  
  /**
   * Perform garbage collection cleanup
   */
  const performGarbageCollection = useCallback(async () => {
    const startTime = performance.now();
    
    try {
      const memoryUsage = getMemoryUsage();
      const memoryUsageMB = memoryUsage.usedHeapSize / (1024 * 1024);
      
      // Check if memory cleanup is needed
      if (memoryUsageMB > memoryConfig.memoryThreshold || 
          loadedChartIds.length > memoryConfig.maxIdleCharts) {
        
        const idleCharts = getIdleCharts();
        
        if (memoryConfig.enableMemoryMonitoring) {
          devLog('Performance GC:', {
            memoryUsage: `${memoryUsageMB.toFixed(1)}MB`,
            threshold: `${memoryConfig.memoryThreshold}MB`,
            loadedCharts: loadedChartIds.length,
            idleCharts: idleCharts.length,
            chartsToUnload: idleCharts
          });
        }
        
        // Unload idle charts
        for (const chartId of idleCharts) {
          unloadChart(chartId);
          lastActivityTime.delete(chartId);
        }
        
        // Force browser garbage collection if available
        if ('gc' in window && typeof (window as any).gc === 'function') {
          (window as any).gc();
        }
      }
      
      // Update performance metrics
      const endTime = performance.now();
      setPerformanceMetrics(prev => ({
        ...prev,
        memoryUsage: getMemoryUsage(),
        chartMetrics: {
          totalCharts: charts.size,
          loadedCharts: loadedChartIds.length,
          activeRenderTime: endTime - startTime,
          lastGcTime: new Date()
        }
      }));
      
    } catch (error) {
      devError('Garbage collection failed:', error);
    }
  }, [
    getMemoryUsage, 
    memoryConfig, 
    loadedChartIds.length, 
    getIdleCharts, 
    unloadChart, 
    lastActivityTime, 
    charts.size
  ]);
  
  /**
   * Update chart activity time
   */
  const updateChartActivity = useCallback((chartId: string) => {
    setLastActivityTime(prev => new Map(prev.set(chartId, new Date())));
  }, []);
  
  /**
   * Optimize chart data for memory efficiency
   */
  const optimizeChartData = useCallback((chart: ChartInstance): ChartInstance => {
    // Create optimized copy with minimal data for inactive charts
    return {
      ...chart,
      // Remove heavy computed data that can be regenerated
      chartData: {
        ...chart.chartData,
        // Keep essential data, remove cached calculations
        parseErrors: [], // Not needed for inactive charts
        apiResponse: undefined // Remove heavy API response data for inactive charts
      }
    };
  }, []);
  
  /**
   * Preload adjacent chart if enabled
   */
  const preloadAdjacentChart = useCallback((direction: 'next' | 'prev') => {
    if (!memoryConfig.preloadNextChart || !activeChartId) return;
    
    const currentIndex = loadedChartIds.indexOf(activeChartId);
    if (currentIndex === -1) return;
    
    const targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    const targetChartId = loadedChartIds[targetIndex];
    
    if (targetChartId && !charts.has(targetChartId)) {
      // Preload logic would go here - for now just update activity
      updateChartActivity(targetChartId);
    }
  }, [memoryConfig.preloadNextChart, activeChartId, loadedChartIds, charts, updateChartActivity]);
  
  // Monitor active chart changes
  useEffect(() => {
    if (activeChartId) {
      updateChartActivity(activeChartId);
      
      // Preload adjacent charts
      if (memoryConfig.preloadNextChart) {
        preloadAdjacentChart('next');
        preloadAdjacentChart('prev');
      }
    }
  }, [activeChartId, updateChartActivity, preloadAdjacentChart, memoryConfig.preloadNextChart]);
  
  // Set up garbage collection timer
  useEffect(() => {
    if (!memoryConfig.enableMemoryMonitoring) return;
    
    if (gcTimerRef.current) {
      clearInterval(gcTimerRef.current);
    }
    
    gcTimerRef.current = setInterval(performGarbageCollection, memoryConfig.gcInterval);
    
    return () => {
      if (gcTimerRef.current) {
        clearInterval(gcTimerRef.current);
      }
    };
  }, [memoryConfig.enableMemoryMonitoring, memoryConfig.gcInterval, performGarbageCollection]);
  
  // Start frame rate monitoring
  useEffect(() => {
    if (memoryConfig.enableMemoryMonitoring) {
      requestAnimationFrame(updateFrameRate);
    }
  }, [memoryConfig.enableMemoryMonitoring, updateFrameRate]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gcTimerRef.current) {
        clearInterval(gcTimerRef.current);
      }
    };
  }, []);
  
  // Expose to window for debugging
  useEffect(() => {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      window.performanceManager = {
        metrics: performanceMetrics,
        config: memoryConfig,
        forceGC: performGarbageCollection,
        getIdleCharts,
        updateConfig: setMemoryConfig
      };
    }
  }, [performanceMetrics, memoryConfig, performGarbageCollection, getIdleCharts]);
  
  return {
    // State
    performanceMetrics,
    memoryConfig,
    
    // Actions
    performGarbageCollection,
    updateChartActivity,
    optimizeChartData,
    updateConfig: setMemoryConfig,
    
    // Utils
    getMemoryUsage,
    getIdleCharts,
    isMemoryPressure: () => {
      const usage = getMemoryUsage();
      return (usage.usedHeapSize / (1024 * 1024)) > memoryConfig.memoryThreshold;
    }
  };
}

/**
 * Performance Manager Provider Component
 */
export interface PerformanceManagerProviderProps {
  children: React.ReactNode;
  config?: Partial<MemoryConfig>;
}

export const PerformanceManagerProvider: React.FC<PerformanceManagerProviderProps> = ({
  children,
  config
}) => {
  const {
    updateChartActivity
  } = usePerformanceManager(config);
  
  const { activeChartId } = useMultipleCharts();
  
  // Track chart activity on active chart changes
  useEffect(() => {
    if (activeChartId) {
      updateChartActivity(activeChartId);
    }
  }, [activeChartId, updateChartActivity]);
  
  return <>{children}</>;
};

const PerformanceManagerExports = {
  usePerformanceManager,
  PerformanceManagerProvider
};

export default PerformanceManagerExports;